import React, { Component } from "react";
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import {
  getServiceTiersAdmin,
  createServiceTier,
  updateServiceTier,
  deleteServiceTier,
  toggleServiceTier,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/Common/ConfirmModal";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { canWrite } from "../../services/PermissionService";
import {
  C, Panel, MiniStat, StatRow, SearchPill, Table, Chip, Btn, IconBtn, MODAL_CSS,
} from "../../components/Console/ConsoleUI";

/**
 * Service tiers — the transport side of the console's settings family.
 *
 * A reskin: the same admin tier list, create, update, toggle and delete calls.
 * The table now shows the tier as the guest meets it — its badge colour, its
 * price and the amenities that justify the price — instead of a feature count.
 *
 * Tiers carry their own icon and colour, chosen from a fixed list by whoever
 * set the tier up. Those are guest-facing choices stored on the record, so the
 * swatch keeps the stored colour rather than being repainted brown.
 */

const CSS = MODAL_CSS;

// Available icons for service tiers
const TIER_ICONS = [
  { value: "ri-car-line", label: "Car" },
  { value: "ri-car-fill", label: "Car Filled" },
  { value: "ri-taxi-line", label: "Taxi" },
  { value: "ri-roadster-line", label: "Roadster" },
  { value: "ri-bus-line", label: "Bus" },
  { value: "ri-truck-line", label: "Truck" },
  { value: "ri-suv-line", label: "SUV" },
  { value: "ri-star-line", label: "Star" },
  { value: "ri-star-fill", label: "Star Filled" },
  { value: "ri-vip-crown-line", label: "Crown" },
  { value: "ri-vip-crown-fill", label: "Crown Filled" },
  { value: "ri-shield-star-line", label: "Shield Star" },
  { value: "ri-medal-line", label: "Medal" },
  { value: "ri-award-line", label: "Award" },
  { value: "ri-diamond-line", label: "Diamond" },
  { value: "ri-service-line", label: "Service" },
];

// Preset colors for service tiers
const TIER_COLORS = [
  { value: "#3d2817", label: "Brand Brown" },
  { value: "#b98a54", label: "Bronze" },
  { value: "#d9c3a5", label: "Sand" },
  { value: "#4caf50", label: "Green" },
  { value: "#c0202b", label: "Red" },
  { value: "#e0a53f", label: "Amber" },
  { value: "#5664d2", label: "Primary Blue" },
  { value: "#1cbb8c", label: "Success Green" },
  { value: "#fcb92c", label: "Warning Yellow" },
  { value: "#9b59b6", label: "Purple" },
  { value: "#2c3e50", label: "Dark Blue" },
  { value: "#3498db", label: "Blue" },
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

class ServiceTiers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tiers: [],
      isLoaded: false,
      showModal: false,
      isEditing: false,
      // View-only filter. It never changes what is fetched.
      search: "",
      currentTier: {
        code: "",
        name: "",
        description: "",
        basePrice: 0,
        nightPrice: 0,
        icon: "ri-car-line",
        color: "#3d2817",
        displayOrder: 0,
        features: [],
        // Capacity is "" (not stated) or a whole number; see handleSave.
        maxPassengers: "",
        maxLuggage: "",
        isPopular: false,
        isActive: true,
      },
      newFeature: "",
      // Delete modal states
      showDeleteModal: false,
      selectedTierId: null,
      deleteLoading: false,
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport", link: "/transport/requests" },
        { title: "Service Tiers", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadTiers();
  }

  loadTiers = () => {
    getServiceTiersAdmin()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ tiers: result.data, isLoaded: true });
        } else {
          this.setState({ isLoaded: true });
          toast.error(result?.message || "Failed to load service tiers");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading service tiers");
      });
  };

  visibleRows = () => {
    const q = this.state.search.trim().toLowerCase();
    const sorted = [...this.state.tiers].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
    );
    if (!q) return sorted;
    return sorted.filter((t) =>
      [t.code, t.name, t.description, ...(t.features || []), t.isActive ? "Active" : "Inactive"]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  };

  columns = () => [
    {
      key: "order",
      label: "#",
      width: 60,
      render: (t) => <span style={{ color: C.taupeLight }}>{t.displayOrder}</span>,
    },
    {
      key: "tier",
      label: "Tier",
      render: (t) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 40, height: 40, borderRadius: 12, flex: "0 0 40px",
              background: t.color || C.brown, color: "#fff",
              display: "grid", placeItems: "center",
            }}
          >
            <i className={`${t.icon || "ri-car-line"} font-size-18`} />
          </span>
          <div>
            <div style={{ fontSize: 15, color: C.ink }}>{t.name}</div>
            <div style={{ marginTop: 2, fontSize: 12, letterSpacing: ".6px", color: C.taupeLight }}>
              {t.code}
              {t.maxPassengers != null && ` · ${t.maxPassengers} pax`}
              {t.maxLuggage != null && ` · ${t.maxLuggage} bags`}
            </div>
            {t.isPopular && (
              <div style={{ marginTop: 4 }}>
                <Chip tone="good">Popular</Chip>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "description",
      label: "Description",
      render: (t) =>
        t.description ? (
          <span style={{ color: C.slate }}>{t.description}</span>
        ) : (
          <span style={{ color: C.taupeLight }}>—</span>
        ),
    },
    {
      key: "pricing",
      label: "Day / night",
      width: 150,
      render: (t) =>
        t.basePrice > 0 || t.nightPrice > 0 ? (
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>{money(t.basePrice)}</div>
            {t.nightPrice > 0 && (
              <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>
                night {money(t.nightPrice)}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: C.taupeLight }}>no surcharge</span>
        ),
    },
    {
      key: "features",
      label: "Included",
      render: (t) =>
        t.features && t.features.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {t.features.slice(0, 3).map((f, i) => (
              <Chip key={i}>{f}</Chip>
            ))}
            {t.features.length > 3 && (
              <span style={{ fontSize: 13, color: C.taupeLight, alignSelf: "center" }}>
                +{t.features.length - 3}
              </span>
            )}
          </div>
        ) : (
          <span style={{ color: C.taupeLight }}>nothing listed</span>
        ),
    },
    {
      key: "status",
      label: "Status",
      width: 110,
      render: (t) =>
        canWrite() ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              this.handleToggle(t._id);
            }}
            style={{ cursor: "pointer" }}
            title="Toggle this tier"
          >
            <Chip tone={t.isActive ? "good" : "neutral"}>{t.isActive ? "Active" : "Off"}</Chip>
          </span>
        ) : (
          <Chip tone={t.isActive ? "good" : "neutral"}>{t.isActive ? "Active" : "Off"}</Chip>
        ),
    },
    ...(canWrite()
      ? [
          {
            key: "actions",
            label: "",
            align: "right",
            width: 100,
            render: (t) => (
              <div
                style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconBtn icon="edit" title="Edit tier" onClick={() => this.handleEdit(t)} />
                <IconBtn icon="delete" title="Delete tier" tone="bad" onClick={() => this.handleDelete(t._id)} />
              </div>
            ),
          },
        ]
      : []),
  ];

  handleAdd = () => {
    this.setState({
      showModal: true,
      isEditing: false,
      currentTier: {
        code: "",
        name: "",
        description: "",
        basePrice: 0,
        nightPrice: 0,
        icon: "ri-car-line",
        color: "#3d2817",
        displayOrder: this.state.tiers.length,
        features: [],
        maxPassengers: "",
        maxLuggage: "",
        isPopular: false,
        isActive: true,
      },
      newFeature: "",
    });
  };

  handleEdit = (tier) => {
    this.setState({
      showModal: true,
      isEditing: true,
      currentTier: {
        ...tier,
        // null means "not stated" on the server; the inputs need a string.
        maxPassengers: tier.maxPassengers == null ? "" : String(tier.maxPassengers),
        maxLuggage: tier.maxLuggage == null ? "" : String(tier.maxLuggage),
        isPopular: !!tier.isPopular,
      },
      newFeature: "",
    });
  };

  handleToggle = (id) => {
    toggleServiceTier(id)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(response.message || "Status updated");
          this.loadTiers();
        } else {
          toast.error(response?.message || "Failed to update status");
        }
      })
      .catch(() => toast.error("Error updating status"));
  };

  handleSave = () => {
    const { currentTier, isEditing } = this.state;

    if (!currentTier.code || !currentTier.name) {
      toast.error("Code and Name are required");
      return;
    }

    // Capacity is optional. Blank goes as "" — the server stores null ("not
    // stated") on create and on update, so clearing a value really clears it.
    const capacity = {};
    for (const [key, label] of [["maxPassengers", "Max passengers"], ["maxLuggage", "Max luggage"]]) {
      const raw = String(currentTier[key] == null ? "" : currentTier[key]).trim();
      if (raw === "") {
        capacity[key] = "";
        continue;
      }
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) {
        toast.error(`${label} must be a whole number of 0 or more`);
        return;
      }
      capacity[key] = n;
    }
    const payload = { ...currentTier, ...capacity, isPopular: !!currentTier.isPopular };

    if (isEditing) {
      updateServiceTier(currentTier._id, payload)
        .then((response) => {
          if (response && response.status === 200) {
            toast.success("Service tier updated successfully");
            this.setState({ showModal: false });
            this.loadTiers();
          } else {
            toast.error(response?.message || "Failed to update service tier");
          }
        })
        .catch(() => toast.error("Error updating service tier"));
    } else {
      createServiceTier(payload)
        .then((response) => {
          if (response && response.status === 200) {
            toast.success("Service tier created successfully");
            this.setState({ showModal: false });
            this.loadTiers();
          } else {
            toast.error(response?.message || "Failed to create service tier");
          }
        })
        .catch(() => toast.error("Error creating service tier"));
    }
  };

  // Delete modal toggle
  toggleDeleteModal = () => this.setState({ showDeleteModal: !this.state.showDeleteModal });

  handleDelete = (id) => {
    this.setState({ selectedTierId: id, showDeleteModal: true });
  };

  confirmDelete = () => {
    const { selectedTierId } = this.state;
    this.setState({ deleteLoading: true });
    deleteServiceTier(selectedTierId)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Service tier deleted");
          this.loadTiers();
        } else {
          toast.error(response?.message || "Failed to delete service tier");
        }
      })
      .catch(() => toast.error("Error deleting service tier"))
      .finally(() => {
        this.setState({ deleteLoading: false, showDeleteModal: false, selectedTierId: null });
      });
  };

  updateField = (field, value) => {
    this.setState({
      currentTier: { ...this.state.currentTier, [field]: value },
    });
  };

  addFeature = () => {
    const { newFeature, currentTier } = this.state;
    if (newFeature.trim()) {
      this.setState({
        currentTier: {
          ...currentTier,
          features: [...(currentTier.features || []), newFeature.trim()],
        },
        newFeature: "",
      });
    }
  };

  removeFeature = (index) => {
    const { currentTier } = this.state;
    const features = [...(currentTier.features || [])];
    features.splice(index, 1);
    this.setState({
      currentTier: { ...currentTier, features },
    });
  };

  render() {
    const { tiers, isLoaded, showModal, isEditing, currentTier, newFeature, search } = this.state;
    const rows = this.visibleRows();

    const active = tiers.filter((t) => t.isActive).length;
    const priced = tiers.filter((t) => t.basePrice > 0 || t.nightPrice > 0);
    const dearest = priced.length
      ? Math.max(...priced.map((t) => Number(t.basePrice || 0)))
      : 0;
    const withFeatures = tiers.filter((t) => (t.features || []).length > 0).length;

    return (
      <ConsolePage
        title="Service tiers"
        subtitle={`Transport · ${tiers.length} tier${tiers.length === 1 ? "" : "s"}${isLoaded ? "" : " · loading…"}`}
        actions={
          <React.Fragment>
            <SearchPill
              value={search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Tier or amenity"
              width={230}
            />
            <Btn
              kind="quiet"
              icon="directions_car"
              onClick={() => this.props.history.push("/transport/vehicle-types")}
            >
              Vehicle types
            </Btn>
            {canWrite() && (
              <Btn icon="add" onClick={this.handleAdd}>
                Add tier
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style>{CSS}</style>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <MiniStat label="Tiers offered" value={active} hint={`${tiers.length - active} switched off`} />
            <MiniStat label="Priced tiers" value={priced.length} hint="carry a base or night rate" />
            <MiniStat label="Highest base rate" value={dearest ? money(dearest) : "—"} hint="across all tiers" />
            <MiniStat
              label="Tiers with amenities"
              value={withFeatures}
              hint={`${tiers.length - withFeatures} list nothing`}
            />
          </StatRow>

          <Panel style={{ padding: "6px 10px" }}>
            {!isLoaded ? (
              <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                Loading tiers…
              </div>
            ) : (
              <Table columns={this.columns()} rows={rows} empty="No service tiers defined yet" />
            )}
          </Panel>
        </div>

        {/* Add/Edit Modal */}
        <Modal
          isOpen={showModal}
          toggle={() => this.setState({ showModal: false })}
          size="lg"
          className="con-modal"
        >
          <ModalHeader toggle={() => this.setState({ showModal: false })}>
            {isEditing ? "Edit service tier" : "Add a service tier"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Tier Code *</Label>
                  <Input
                    type="text"
                    value={currentTier.code}
                    onChange={(e) => this.updateField("code", e.target.value.toUpperCase())}
                    placeholder="e.g., STANDARD, COMFORT, VIP"
                    maxLength={20}
                  />
                  <small style={{ color: C.taupeLight }}>Unique identifier (auto-uppercased)</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Tier Name *</Label>
                  <Input
                    type="text"
                    value={currentTier.name}
                    onChange={(e) => this.updateField("name", e.target.value)}
                    placeholder="e.g., Standard, Comfort, VIP"
                  />
                </FormGroup>
              </Col>
              <Col xs={12}>
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={currentTier.description}
                    onChange={(e) => this.updateField("description", e.target.value)}
                    placeholder="Brief description of this service tier"
                  />
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SectionTitle>Pricing</SectionTitle>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Base Price ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentTier.basePrice}
                    onChange={(e) => this.updateField("basePrice", parseFloat(e.target.value) || 0)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Night Price ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentTier.nightPrice}
                    onChange={(e) => this.updateField("nightPrice", parseFloat(e.target.value) || 0)}
                  />
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SectionTitle>Appearance</SectionTitle>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Icon</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Input
                      type="select"
                      value={currentTier.icon}
                      onChange={(e) => this.updateField("icon", e.target.value)}
                      style={{ flex: 1 }}
                    >
                      {TIER_ICONS.map((icon) => (
                        <option key={icon.value} value={icon.value}>
                          {icon.label}
                        </option>
                      ))}
                    </Input>
                    <span
                      style={{
                        width: 46, height: 46, borderRadius: 12, flex: "0 0 46px",
                        background: currentTier.color || C.brown, color: "#fff",
                        display: "grid", placeItems: "center",
                      }}
                    >
                      <i className={`${currentTier.icon} font-size-18`} />
                    </span>
                  </div>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Colour</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Input
                      type="select"
                      value={currentTier.color}
                      onChange={(e) => this.updateField("color", e.target.value)}
                      style={{ flex: 1 }}
                    >
                      {TIER_COLORS.map((color) => (
                        <option key={color.value} value={color.value}>
                          {color.label}
                        </option>
                      ))}
                    </Input>
                    <span
                      style={{
                        width: 46, height: 46, borderRadius: 12, flex: "0 0 46px",
                        background: currentTier.color || C.brown,
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    min="0"
                    value={currentTier.displayOrder}
                    onChange={(e) => this.updateField("displayOrder", parseInt(e.target.value) || 0)}
                  />
                  <small style={{ color: C.taupeLight }}>Lower numbers appear first</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup check style={{ marginTop: 28 }}>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={currentTier.isActive}
                      onChange={(e) => this.updateField("isActive", e.target.checked)}
                    />
                    {" "}Tier is Active
                  </Label>
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SectionTitle>Capacity</SectionTitle>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Max passengers</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={currentTier.maxPassengers}
                    onChange={(e) => this.updateField("maxPassengers", e.target.value)}
                    placeholder="Not stated"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Max luggage</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={currentTier.maxLuggage}
                    onChange={(e) => this.updateField("maxLuggage", e.target.value)}
                    placeholder="Not stated"
                  />
                  <small style={{ color: C.taupeLight }}>Bags. Leave blank to hide it in the app</small>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup check style={{ marginTop: 28 }}>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={!!currentTier.isPopular}
                      onChange={(e) => this.updateField("isPopular", e.target.checked)}
                    />
                    {" "}Mark as Popular
                  </Label>
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SectionTitle>Features &amp; amenities</SectionTitle>
              </Col>
              <Col xs={12}>
                <FormGroup>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Input
                      type="text"
                      value={newFeature}
                      onChange={(e) => this.setState({ newFeature: e.target.value })}
                      placeholder="Add a feature (e.g., Wi-Fi, Water bottle, etc.)"
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), this.addFeature())}
                    />
                    <Btn icon="add" onClick={this.addFeature} style={{ flex: "0 0 auto" }}>
                      Add
                    </Btn>
                  </div>
                </FormGroup>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(currentTier.features || []).map((feature, index) => (
                    <span
                      key={index}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 7,
                        padding: "6px 12px", borderRadius: 11,
                        background: C.iconTint, color: C.brown, fontSize: 13,
                      }}
                    >
                      {feature}
                      <span
                        className="ms"
                        style={{ fontSize: 16, cursor: "pointer", color: C.taupe }}
                        onClick={() => this.removeFeature(index)}
                      >
                        close
                      </span>
                    </span>
                  ))}
                  {(!currentTier.features || currentTier.features.length === 0) && (
                    <span style={{ fontSize: 14, color: C.taupeLight }}>No features added yet</span>
                  )}
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={() => this.setState({ showModal: false })}>
              Cancel
            </Btn>
            <Btn onClick={this.handleSave}>
              {isEditing ? "Update tier" : "Add tier"}
            </Btn>
          </ModalFooter>
        </Modal>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={this.confirmDelete}
          title="Delete Service Tier"
          message="Are you sure you want to delete this service tier? This action cannot be undone."
          confirmText="Delete"
          confirmColor="danger"
          icon="ri-delete-bin-line"
          loading={this.state.deleteLoading}
        />
      </ConsolePage>
    );
  }
}

const SectionTitle = ({ children }) => (
  <div
    style={{
      margin: "6px 0 16px",
      paddingTop: 16,
      borderTop: `1px solid ${C.tint}`,
      fontSize: 12,
      letterSpacing: ".8px",
      textTransform: "uppercase",
      color: C.taupe,
    }}
  >
    {children}
  </div>
);

export default ServiceTiers;
